/**
 * Naming a variety of the gardener's own, and writing the garden's plants: adding, replacing, archiving and deleting
 * for good.
 *
 * Reading the garden's plants is api/plants; reading what the pickers choose from is api/plantPicker (W24 Job B: the
 * Plant, Type and Variety drop-downs read the server's three small answers; the whole catalogue, GET /plants/all and
 * GET /varieties/all, is no longer read). This is what the add and edit dialogs write through.
 *
 * Endpoints:
 * - POST   /varieties { plantId, name } → { success, variety }.
 * - POST   /plants/obj/add PlantWritePayload → WriteResponse (with the
 *   new plant's id;
 *   402 FREE_PLANT_LIMIT on Free past its plants: the subscription wall).
 *   A plant whose Type is "Not sure" (B52) is sent as { cropId, typeUnsure: true } with no variety: the server picks the
 *   crop's usual type and files the plant flagged.
 * - POST   /plants/update { plantObjId, newVarietyId?, newLocationId?,
 *   newStatus?, cropId?, typeUnsure? } → WriteResponse (with `twins` when the variety, type or bed changed, D66).
 *   A variety chosen clears the flag on the server.
 * - PATCH  /plants/:plantObjId/status { status } → WriteResponse.
 * - DELETE /plants/:plantObjId { confirm: 'DELETE' } → DeleteResult.
 *
 * Who uses it: shared/components/plant-fields (PlantIdentityFields names a variety), shared/context/PlantsContext and
 * the plant list, locations and season screens. Caching: none of its own; naming a variety marks the picker's answers
 * stale (usePlantPicker pickerStale), and the writes invalidate 'plants' and its neighbours (PlantsContext).
 * Auth: the signed-in token; writes land in the garden being tended.
 *
 * @module api/plantCatalogue
 */

import { apiClient } from './apiClient';
import { trackEvent } from '@/shared/lib/analytics';
import { growLimit } from '@/shared/lib/growSettings';

/** A variety as the server names it back: its id and name. */
export interface CatalogueItem {
  /** The variety's id. */
  id: number;
  /** Its name. */
  name: string;
  /** The "Not sure" entry, which stands for the plant's default. */
  is_placeholder?: boolean;
}

/** The kinds a plant group can be. Trees will join these. */
export type PlantKind = 'vegetable' | 'herb' | 'fruit' | 'flower';

/** A named variety and the plant it belongs to. */
export interface CatalogueVariety extends CatalogueItem {
  /** The catalogue plant it is a variety of. */
  plantId: number;
  /** The gardener's own, named by them (a seed packet's, say), rather than Grow's shared list. */
  own?: boolean;
}

/**
 * Names a variety of a plant: the one Grow or the gardener already has by
 * that name (capitals aside), or a new one of their own.
 *
 * @param plantId - The catalogue plant it belongs to.
 * @param name - What they typed.
 * @returns The variety, ready to choose.
 */
export async function addOwnVariety(plantId: number, name: string): Promise<CatalogueVariety> {
  const response = await apiClient.post<{ success: boolean; variety: CatalogueVariety }>('/varieties', { plantId, name });
  return response.data.variety;
}

/** What the create and update endpoints accept. */
export interface PlantWritePayload {
  /** The variety (the "Not sure" placeholder stands for the plant's default); left out when typeUnsure. */
  varietyId?: number;
  /** With typeUnsure: the crop (plant group) whose usual type the server files the plant on (B52). */
  cropId?: number;
  /** Type is "Not sure" (B52): the server files the plant on the crop's usual type and flags it. No varietyId is sent. */
  typeUnsure?: boolean;
  /** The bed. */
  locationId: number;
  /*
   * The four below are sent but not stored on create: the backend's
   * /plants/obj/add keeps only the variety and the bed (AddPlantObj). A
   * plant's stage is its stage history (api/stageMarks), recorded apart.
   */
  /** A growth stage; not stored on create (see above). */
  stageId?: number | null;
  /** When the plant entered that stage, as YYYY-MM-DD; not stored on create. */
  stageStartDate?: string | null;
  /** A transplant rather than sown; not stored on create. */
  isTransplant?: boolean;
  /** PLANT_STATUS: growing or archived; not stored on create. */
  status?: number;
}

/** Both endpoints answer with a success flag rather than the saved row. */
interface WriteResponse {
  /** The server's flag. */
  success: boolean;
  /** The new plant's id (create), sometimes as a string. */
  plant_obj_id?: number | string;
  /** The server's sentence, in English. */
  message?: string;
  /**
   * POST /plants/update only (D66): the ids of the other growing plants of the same crop now in
   * the edited plant's bed, when its variety or bed changed; [] otherwise. Never on an add.
   */
  twins?: number[];
}

/**
 * Adds a plant to the garden being tended. The backend also starts the
 * trial's clock at a gardener's first plant.
 *
 * @param payload - Variety (or, for "Not sure" Type, the crop and typeUnsure) and location are required, and are all the backend stores.
 * @returns The success flag, and the new plant's id when the server sends it.
 */
export async function createPlant(payload: PlantWritePayload): Promise<WriteResponse> {
  const response = await apiClient.post<WriteResponse>('/plants/obj/add', {
    varietyId: payload.typeUnsure ? undefined : payload.varietyId,
    cropId: payload.typeUnsure ? payload.cropId : undefined,
    typeUnsure: payload.typeUnsure ? true : undefined,
    locationId: payload.locationId,
    stageId: payload.stageId ?? undefined,
    stageStartDate: payload.stageStartDate ?? undefined,
    isTransplant: payload.isTransplant ?? undefined,
    status: payload.status ?? undefined,
  });
  if (response.data.success) trackEvent('plant_added', { how: 'form' });
  return response.data;
}

/**
 * Changes an existing plant's variety (or its type, back to "Not sure"), location or status.
 *
 * A patch: only what is given changes, and anything left out keeps its value
 * on the server (/plants/update writes only the fields it is sent). Stage is
 * not changed here; it lives in the plant's stage history.
 *
 * The endpoint is a POST rather than a PUT and every field is named new*, so
 * the mapping is spelled out here rather than at each call site.
 *
 * @param plantObjId - The plant object being changed.
 * @param payload - What to change; the rest is left as it is.
 */
export async function updatePlant(
  plantObjId: number,
  payload: Partial<Pick<PlantWritePayload, 'varietyId' | 'locationId' | 'status' | 'cropId' | 'typeUnsure'>>
): Promise<WriteResponse> {
  const response = await apiClient.post<WriteResponse>('/plants/update', {
    plantObjId,
    newVarietyId: payload.typeUnsure ? undefined : payload.varietyId ?? undefined,
    cropId: payload.typeUnsure ? payload.cropId : undefined,
    typeUnsure: payload.typeUnsure ? true : undefined,
    newLocationId: payload.locationId ?? undefined,
    newStatus: payload.status ?? undefined,
  });
  return response.data;
}

/** plant_obj.status. Locations use the same pair. The database's values, not tunables. */
export const PLANT_STATUS = { ACTIVE: 1, ARCHIVED: 0 } as const;

/**
 * Archives a plant, or restores one. Nothing is deleted.
 *
 * A plant carries photos, notes and a timeline, and the database refuses to
 * cascade a delete through plant_timelines anyway. Removing one from the garden
 * marks it inactive and leaves the record whole, so an archive view can read it
 * back and a mistake can be undone.
 *
 * @param plantObjId - The plant to archive or restore.
 * @param status - ARCHIVED to remove it from the garden, ACTIVE to bring it back.
 */
export async function setPlantStatus(
  plantObjId: number,
  status: typeof PLANT_STATUS[keyof typeof PLANT_STATUS]
): Promise<WriteResponse> {
  const response = await apiClient.patch<WriteResponse>(`/plants/${plantObjId}/status`, { status });
  return response.data;
}

/** What the permanent delete reports back. */
export interface DeleteResult {
  /** The server's flag. */
  success: boolean;
  /** The plant removed. */
  plantObjId: number;
  /** How many photos went with it. */
  photos: number;
  /** How many notes went with it. */
  notes: number;
}

/**
 * Removes an archived plant for good, with everything hanging off it.
 *
 * The opposite of `setPlantStatus`: that one takes a plant out of the garden
 * and keeps its photographs, notes and timeline. This keeps nothing, and the
 * files go from storage too.
 *
 * The word travels in the body because the server insists on it, the same as
 * deleting an account — a request that cannot be made by accident.
 *
 * @param plantObjId - The plant to remove. Must already be archived.
 */
export async function deletePlantForever(plantObjId: number): Promise<DeleteResult> {
  const response = await apiClient.delete<DeleteResult>(`/plants/${plantObjId}`, {
    data: { confirm: growLimit('DELETE_CONFIRM_WORD') },
  });
  return response.data;
}
